import { z } from 'zod';

// 台灣縣市列表
export const taiwanCities = [
  '台北市', '新北市', '桃園市', '台中市', '台南市', '高雄市',
  '基隆市', '新竹市', '嘉義市',
  '新竹縣', '苗栗縣', '彰化縣', '南投縣', '雲林縣', '嘉義縣',
  '屏東縣', '宜蘭縣', '花蓮縣', '台東縣', '澎湖縣', '金門縣', '連江縣'
];

// 付款方式選項
export const paymentMethods = [
  { value: 'credit_card', label: '信用卡', description: '支援 Visa、Mastercard、JCB' },
  { value: 'linepay', label: 'LINE Pay', description: '使用 LINE Pay 快速付款' },
  { value: 'atm', label: 'ATM 轉帳', description: '匯款至指定帳號，3 天內完成轉帳' },
  { value: 'cvs', label: '超商代碼繳費', description: '7-11、全家、萊爾富、OK 繳費' },
  { value: 'cvs_pickup_cod', label: '超商取貨付款', description: '超商取貨時付款（需運費+手續費）' },
  { value: 'cvs_pickup_prepaid', label: '超商取貨（先付款）', description: '線上付款後超商取貨' },
  { value: 'cod', label: '宅配貨到付款', description: '宅配到府，收到商品後再付款' }
];

// 手機號碼驗證 (台灣格式)
const taiwanPhoneRegex = /^09\d{8}$/;

// 郵遞區號驗證
const zipCodeRegex = /^\d{3,5}$/;

// 結帳表單 Schema
export const CheckoutFormSchema = z.object({
  // 收件人資訊
  recipient_name: z
    .string()
    .min(2, '姓名至少需要 2 個字')
    .max(50, '姓名不能超過 50 個字')
    .regex(/^[\u4e00-\u9fa5a-zA-Z\s]+$/, '姓名只能包含中文、英文和空格'),
  
  recipient_phone: z
    .string()
    .regex(taiwanPhoneRegex, '請輸入有效的手機號碼（格式：09XXXXXXXX）'),
  
  recipient_email: z
    .string()
    .email('請輸入有效的 Email 地址')
    .toLowerCase(),
  
  // 收件地址
  city: z
    .string()
    .refine((val) => taiwanCities.includes(val), '請選擇有效的縣市'),
  
  district: z
    .string()
    .min(2, '請輸入區域')
    .max(20, '區域名稱過長'),
  
  zip_code: z
    .string()
    .regex(zipCodeRegex, '請輸入有效的郵遞區號（3-5 碼數字）'),
  
  address: z
    .string()
    .min(10, '詳細地址至少需要 10 個字')
    .max(200, '詳細地址不能超過 200 個字'),
  
  // 付款方式
  payment_method: z
    .enum(['credit_card', 'linepay', 'atm', 'cvs', 'cvs_pickup_cod', 'cvs_pickup_prepaid', 'cod'], {
      errorMap: () => ({ message: '請選擇付款方式' }),
    }),
  
  // 超商取貨資訊（選填，超商取貨時必填）
  cvs_store_name: z
    .string()
    .max(100, '超商門市名稱不能超過 100 個字')
    .optional()
    .or(z.literal('')),
  
  cvs_store_address: z
    .string()
    .max(200, '超商門市地址不能超過 200 個字')
    .optional()
    .or(z.literal('')),
  
  // 備註（選填）
  notes: z
    .string()
    .max(500, '備註不能超過 500 個字')
    .optional()
    .or(z.literal('')),
  
  // 同意條款
  agree_terms: z
    .boolean()
    .refine((val) => val === true, '請閱讀並同意服務條款與隱私權政策'),
});

// 結帳表單預設值
export const checkoutFormDefaults = {
  recipient_name: '',
  recipient_phone: '',
  recipient_email: '',
  city: '',
  district: '',
  zip_code: '',
  address: '',
  payment_method: 'credit_card',
  cvs_store_name: '',
  cvs_store_address: '',
  notes: '',
  agree_terms: false,
};

// 聯絡表單 Schema（用於客服聯繫等）
export const ContactFormSchema = z.object({
  name: z
    .string()
    .min(2, '姓名至少需要 2 個字')
    .max(50, '姓名不能超過 50 個字'),
  
  email: z
    .string()
    .email('請輸入有效的 Email 地址')
    .toLowerCase(),
  
  phone: z
    .string()
    .regex(taiwanPhoneRegex, '請輸入有效的手機號碼（格式：09XXXXXXXX）')
    .optional()
    .or(z.literal('')),
  
  subject: z
    .string()
    .min(2, '主旨至少需要 2 個字')
    .max(100, '主旨不能超過 100 個字'),
  
  message: z
    .string()
    .min(10, '訊息至少需要 10 個字')
    .max(1000, '訊息不能超過 1000 個字'),
});

// 折扣碼驗證 Schema
export const DiscountCodeSchema = z.object({
  code: z
    .string()
    .min(3, '折扣碼至少需要 3 個字')
    .max(20, '折扣碼不能超過 20 個字')
    .toUpperCase()
    .regex(/^[A-Z0-9]+$/, '折扣碼只能包含大寫英文和數字'),
});

// 訂單查詢 Schema
export const OrderQuerySchema = z.object({
  order_id: z
    .string()
    .regex(/^NMN-\d+-[A-Z0-9]+$/, '請輸入有效的訂單編號（格式：NMN-xxxxx-XXXXXX）'),
  
  email: z
    .string()
    .email('請輸入有效的 Email 地址')
    .toLowerCase(),
});

// 數量輸入驗證
export const QuantitySchema = z
  .number()
  .int('數量必須是整數')
  .min(1, '數量至少為 1')
  .max(99, '數量不能超過 99');

// 驗證台灣手機號碼
export const validateTaiwanPhone = (phone) => {
  return taiwanPhoneRegex.test(phone);
};

// 驗證 Email
export const validateEmail = (email) => {
  return z.string().email().safeParse(email).success;
};

// 驗證郵遞區號
export const validateZipCode = (zipCode) => {
  return zipCodeRegex.test(zipCode);
};

// 格式化手機號碼（加入空格）
export const formatPhone = (phone) => {
  if (!phone) return '';
  const cleaned = phone.replace(/\D/g, '');
  if (cleaned.length === 10) {
    return `${cleaned.slice(0, 4)} ${cleaned.slice(4, 7)} ${cleaned.slice(7)}`;
  }
  return phone;
};

// 清理手機號碼（移除空格和特殊字元）
export const cleanPhone = (phone) => {
  return phone.replace(/\D/g, '');
};

// 驗證折扣碼格式
export const validateDiscountCode = (code) => {
  return DiscountCodeSchema.safeParse({ code }).success;
};
